import { z } from 'zod'
import { AxiosInstance, AxiosRequestConfig, isAxiosError } from 'axios'

import { isZodError } from '../../types/typesUtils'
import { LogFunctions } from 'electron-log'
import { useGtm } from '@composables/useGtm'
import { useEvent } from '@composables/useEventBus'
import { extractCustomError } from '@lib/utils/gtmUtils'
import { logErrorDebug } from '@lib/utils/logUtils'
import { LogBody, LogKind, LogOwnLevel } from '@lib/models/internal/log'

export abstract class BaseService {
  abstract readonly client: AxiosInstance

  protected async request<T extends z.ZodType>(
    config: AxiosRequestConfig,
    schema: T,
    logger: LogFunctions,
    { mustTrackCustomError = true } = {}
  ): Promise<z.infer<T>> {
    try {
      const res = await this.client(config)
      return await schema.parseAsync(res.data)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const { trackCustomError } = useGtm()
      const requestUrl = `${this.client.defaults.baseURL}${config.url}`

      if (isAxiosError(error)) {
        const { response } = error
        const errData = response?.data
        const errStatus = response?.status ?? 500
        // Not sanitized here - buildFileFormat (src/main/logFileFormat.ts) is the single
        // sanitization point, applied to the reconstructed envelope right before it's
        // written to the physical file. See axiosUtils.ts for the same reasoning.
        const data: LogBody = {
          type: LogOwnLevel.ERROR,
          kind: LogKind.AXIOS_ERROR,
          data: {
            status: errStatus,
            requestUrl,
            response: errData || error.message
          }
        }
        logErrorDebug(logger, data)
        if (errStatus === 401) {
          useEvent('customer:unauthorized')
        }
      } else if (isZodError(error)) {
        const data: LogBody = {
          type: LogOwnLevel.ERROR,
          kind: LogKind.ZOD_ERROR,
          data: {
            requestUrl,
            errors: error.errors
          }
        }
        logErrorDebug(logger, data)
      } else {
        const data: LogBody = {
          type: LogOwnLevel.ERROR,
          kind: LogKind.ERROR,
          data: {
            requestUrl,
            message: error.message
          }
        }
        logErrorDebug(logger, data)
      }

      if (mustTrackCustomError) {
        trackCustomError(
          extractCustomError({
            error
          })
        )
      }

      throw error
    }
  }
}
